import { FC, useState } from 'react'
import { ChevronDownIcon, FoldVertical } from 'lucide-react'
import { JsonCode } from '@/components/ai-elements/json-code'
import type { CompactionItem } from './timeline'

/** doc 123:压缩折叠条 — 灰色样式,默认收起,点击展开摘要 markdown 渲染。 */
export const CompactionBanner: FC<{ item: CompactionItem }> = ({ item }) => {
  const [open, setOpen] = useState(false)
  const label = `已压缩历史 · ${item.headDropped} 条 → 摘要`
  return (
    <div className="my-2 shrink-0 overflow-hidden rounded-md border bg-muted/40 text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center gap-2 px-3 py-1.5 text-left text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      >
        <FoldVertical className="size-4 shrink-0" />
        <span className="grow">{label}</span>
        <span className="text-[10px] opacity-60">{item.trigger}</span>
        <ChevronDownIcon
          className={`size-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t bg-background/50 px-3 py-2">
          <SummaryBody summary={item.summary} />
        </div>
      )}
    </div>
  )
}

const SummaryBody: FC<{ summary: string }> = ({ summary }) => {
  // 渲染 markdown 简单结构:## 标题 → <h3>、列表 → <ul>、代码块 → <pre>
  // 摘要里如果包含 JSON 围栏(block)尝试 JsonCode 渲染
  const fenceMatch = summary.match(/```json\s*([\s\S]+?)\s*```/i)
  if (fenceMatch) {
    try {
      JSON.parse(fenceMatch[1]) // 仅验证可解析
      const before = summary.slice(0, fenceMatch.index).trim()
      const after = summary.slice(fenceMatch.index! + fenceMatch[0].length).trim()
      return (
        <div className="space-y-2">
          {before && <MarkdownText text={before} />}
          <JsonCode code={fenceMatch[1]} />
          {after && <MarkdownText text={after} />}
        </div>
      )
    } catch {
      // 解析失败退回普通渲染
    }
  }
  return <MarkdownText text={summary} />
}

const MarkdownText: FC<{ text: string }> = ({ text }) => {
  const lines = text.split('\n')
  return (
    <div className="space-y-1 text-xs leading-relaxed text-foreground/85">
      {lines.map((line, i) => {
        if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-semibold">{line.slice(3)}</h3>
        if (line.startsWith('### ')) return <h4 key={i} className="text-xs font-semibold">{line.slice(4)}</h4>
        if (/^[-*]\s+/.test(line)) return <li key={i} className="ml-4 list-disc">{line.replace(/^[-*]\s+/, '')}</li>
        if (/^\d+\.\s+/.test(line)) return <li key={i} className="ml-4 list-decimal">{line.replace(/^\d+\.\s+/, '')}</li>
        if (line.trim() === '') return <div key={i} className="h-1" />
        return <p key={i}>{line}</p>
      })}
    </div>
  )
}
